"use client";

// ── The verdict pill, left of Submit in the editor's bottom bar (`EX-EDR01`) ─
// Wave 23 decision 1: the verdict no longer takes a card's height out of the
// editor. It is one line in the bar, and the details float over the editor
// when the pill is pressed (`ResultCard`). Before a first submission it says
// so and is not a button, because there is nothing to open.

import { CheckCircle2, Circle, Loader2, AlertTriangle, XCircle } from "lucide-react";
import { useTranslations } from "next-intl";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { verdictTone } from "../../lib/format";
import type { PillState } from "./workspace-result";

const PILL = "inline-flex h-10 min-w-0 items-center gap-2 rounded-full px-3.5 text-sm font-semibold";

export function VerdictPill({
  state,
  expanded,
  onToggle,
  controls,
}: {
  state: PillState;
  expanded: boolean;
  onToggle: () => void;
  /** The id of the card the pill opens, for `aria-controls`. */
  controls: string;
}) {
  const t = useTranslations("judge");
  const tEditor = useTranslations("judge.editor");

  if (state.kind === "none") {
    return (
      <span className={cn(PILL, "bg-white/5 font-medium text-white/55")} data-testid="verdict-pill">
        <Circle className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span className="truncate">{tEditor("pillNone")}</span>
      </span>
    );
  }

  const pending = state.kind === "queued" || state.kind === "judging";
  const tone = pending
    ? "pending"
    : state.kind === "compile"
      ? "warning"
      : verdictTone(state.verdict);

  return (
    <Button
      type="button"
      variant={null}
      size="pill"
      onClick={onToggle}
      aria-expanded={expanded}
      aria-controls={controls}
      data-testid="verdict-pill"
      className={cn(
        "min-w-0 justify-start ring-1 ring-inset",
        tone === "pending" && "bg-white/5 text-white/70 ring-white/10 hover:bg-white/10",
        tone === "success" && "bg-emerald-400/10 text-emerald-300 ring-emerald-400/35 hover:bg-emerald-400/15",
        tone === "warning" && "bg-amber-400/10 text-amber-300 ring-amber-400/35 hover:bg-amber-400/15",
        tone === "error" && "bg-red-400/10 text-red-300 ring-red-400/35 hover:bg-red-400/15",
        tone === "neutral" && "bg-white/5 text-white/70 ring-white/10 hover:bg-white/10",
      )}
    >
      {pending ? (
        <Loader2 className="h-4 w-4 shrink-0 motion-safe:animate-spin" aria-hidden />
      ) : state.kind === "compile" ? (
        <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
      ) : tone === "success" ? (
        <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />
      ) : (
        <XCircle className="h-4 w-4 shrink-0" aria-hidden />
      )}
      <span className="truncate">
        {state.kind === "queued"
          ? tEditor("pillQueued")
          : state.kind === "judging"
            ? tEditor("pillJudging")
            : state.kind === "compile"
              ? t("verdict.compilation_error")
              : t(`verdict.${state.verdict}`)}
      </span>
      {state.kind === "verdict" && state.total !== null && state.passed !== null ? (
        <span className="shrink-0 font-medium tabular-nums text-white/70">
          {tEditor("pillCount", { passed: state.passed, total: state.total })}
        </span>
      ) : null}
    </Button>
  );
}
